// Typed client for binders and sealed product (mtg-api
// docs/superpowers/specs/2026-10-18-binders-sealed-api.md; wire fixtures in
// src/api/__fixtures__/copies_wire/ binders_*, sealed_*).
//
// Every owner route goes through copiesFetch (the same bearer, 401 refresh,
// 429/503 backoff and op_key rules as the copies routes) with
// `capabilityNeutral`: a binder or sealed answer never moves the per-copy
// capability. 503 binders_not_ready is its own error kind (never retried) and
// hides the binders UI. Money stays a decimal STRING end to end.
//
// The public sealed catalog (GET /sealed/catalog) is NOT under the
// collection: it is fetched here directly, with the owner's bearer when there
// is one (the catalog is dark unless COLLECTION_COPIES is on globally or the
// bearer belongs to an allowlisted user).
import { apiFetch } from '../i18n/apiFetch'
import { apiUrl } from './apiUrl'
import { writeAuthFor } from '../lib/accessToken'
import {
  CopiesError, classifyFailure, copiesFetch, parseDetail, parseErrorField, responseJson, type CopiesContext,
} from './collectionCopies'
import { ERR, HTTP, SEG } from './collectionCopies.codes'
import {
  BINDER_LIMITS_DEFAULT, BUY_SRC, CATALOG_QUERY, DECK_BOX_FLAGS, DECK_OUTBOX_STATES, MATCH_ALL, SPARE_GATED_RULE_FIELDS, UNKNOWN,
  type BinderColor,
  type BinderKind, type DeckBoxFlag, type DeckOutboxState, type SealedStatus,
} from './collectionBinders.codes'
import { decodeBinderShare, type BinderShare } from './binderShare'

// --- Wire types ---------------------------------------------------------------

export interface BinderCover {
  scryfall_id: string
  /** The printing's art crop (falling back to its normal image); null if neither. */
  image_art_crop: string | null
}

export interface RuleCondition {
  field: string
  op: string
  /**
   * A list for `in` / color ops; a decimal string for price, an ISO date for
   * added, a string for name, and a JSON NUMBER for mv, copies and
   * printing_copies.
   */
  value: string | string[] | number
  /**
   * Any other key a newer server sends, kept with its JSON value: such a
   * condition is read-only in the builder and sent back exactly as saved.
   */
  [key: string]: unknown
}

/** The condition keys this client knows; any other key keeps a condition exactly as sent. */
export const KNOWN_CONDITION_KEYS: readonly string[] = ['field', 'op', 'value']

export interface BinderRules {
  match: typeof MATCH_ALL
  conditions: RuleCondition[]
}

export interface Binder {
  /** null for the virtual Unsorted binder. */
  binder_id: number | null
  kind: BinderKind
  name: string
  color: BinderColor | null
  cover: BinderCover | null
  position: number | null
  /**
   * null when the server could not count this binder (`stats_available:
   * false`, a spare binder whose My Decks read failed). NEVER read as 0:
   * an unavailable binder is not an empty one (statsUnavailable).
   */
  line_count: number | null
  copy_count: number | null
  /**
   * Owned + trade value, decimal string; null when nothing in it is priced,
   * or when its stats are unavailable (tell the two apart with
   * statsUnavailable).
   */
  value_usd: string | null
  /**
   * `false` = the server could not count this binder (its counts are null);
   * absent = available. Only ever set to false. A local `stats_pending`
   * (below) is this client's own, never the wire's.
   */
  stats_available?: false
  /**
   * Local only: My Decks changed since these counts were read and a re-read
   * is out (invalidateDeckDependentCollectionData). The counts are null.
   */
  stats_pending?: true
  updated_at: string | null
  rules?: BinderRules
  rules_rev?: number
  /**
   * Deck boxes (`kind: 'deck'` only, deck-boxes spec 3): the ManaBox deck
   * label, its source_key, the runner state and the last commit's flags. A
   * box never carries its generation or manifest (read the generation with
   * the runner's `check`). A dangling box (its saved deck deleted) is
   * `outbox_state: 'done'`, `flags: []`: "Deck deleted" is the client's own
   * join against saved decks.
   */
  import_name?: string
  source_key?: string
  outbox_state?: DeckOutboxState
  flags?: DeckBoxFlag[]
  /**
   * The binder's live public link (binder share, Phase 1), or null when it
   * has none. Every decoded binder carries it (absent on the wire = null);
   * optional only so a binder built locally need not spell it out.
   */
  share?: BinderShare | null
}

export interface BinderLimits {
  manual: number
  smart: number
  /**
   * The deck box cap. Present ONLY when the collection's deck gate is on, so
   * its absence also means "do not offer the deck smart rule".
   */
  deck?: number
  /**
   * Present ONLY when the spare rule's gate is on for this collection: its
   * absence means "do not offer the spare smart rule". The value (a number,
   * or `true`) is not a cap this client reads; presence is the signal.
   */
  spare?: number | true
}

export interface BindersList {
  binders_rev: number
  copies_rev: number
  prices_as_of: string | null
  binders: Binder[]
  /** The server's per-collection limits; BINDER_LIMITS_DEFAULT when it sent none. */
  limits: BinderLimits
  /**
   * The binder share gate is on for this owner: offer "Share" on manual and
   * smart binders. Absent (an older server, the gate off) = false.
   */
  share_enabled: boolean
}

export interface BinderWriteResponse {
  binder: Binder
  binders_rev: number
}

export interface BinderPreview {
  line_count: number
  copy_count: number
  value_usd: string | null
}

export interface BinderCreateBody {
  kind: 'manual' | 'smart'
  name: string
  color: BinderColor
  cover?: { scryfall_id: string } | null
  rules?: BinderRules
}

export interface BinderPatchBody {
  name?: string
  color?: BinderColor
  cover?: { scryfall_id: string } | null
  rules?: BinderRules
}

export interface SealedItem {
  sealed_id: number
  product_uuid: string | null
  name: string
  set_code: string | null
  category: string | null
  qty: number
  status: SealedStatus
  /** Per unit, as typed; "0.00" is a real $0; null = not entered. */
  purchase_price: string | null
  unit_value_usd: string | null
  value_usd: string | null
  paid_usd: string | null
  gain_usd: string | null
  stale: boolean
  /** An INTERNAL API path (/sealed/redirect/{uuid}), or null. */
  buy_url: string | null
  /**
   * A product photo (TCGplayer CDN), null for free-text rows and products with
   * no TCGplayer id. Optional: absent from servers that predate it.
   */
  image_url?: string | null
  added_at: string
  updated_at: string
}

export interface SealedTotals {
  paid_usd: string | null
  value_usd: string | null
  unpriced_rows: number
  unpriced_units: number
}

export interface SealedList {
  items: SealedItem[]
  totals: SealedTotals
}

export interface SealedWriteResponse {
  item: SealedItem | null
  merged: boolean
  result_sealed_id: number | null
  /** PATCH only: qty 0 deleted the row. */
  deleted?: boolean
}

export interface SealedOpenResponse {
  opened: number
  remaining: number
  /** null when the last unit was opened (the row is gone). */
  sealed_id: number | null
}

export interface SealedAddBody {
  product_uuid?: string
  name?: string
  set_code?: string
  category?: string
  qty: number
  status?: SealedStatus
  purchase_price?: string
}

export interface SealedPatchBody {
  qty?: number
  status?: SealedStatus
  purchase_price?: string | null
  expected_qty?: number
}

export interface SealedProduct {
  uuid: string
  name: string
  set_code: string | null
  category: string | null
  subtype: string | null
  release_date: string | null
  ck_retail_usd: string | null
  price_updated_at: string | null
  stale: boolean
  /** A product photo (TCGplayer CDN), or null. Optional: older servers omit it. */
  image_url?: string | null
}

// --- Decoders -----------------------------------------------------------------

type Obj = Record<string, unknown>

function fail(): never {
  throw new CopiesError(ERR.decode)
}
function obj(v: unknown): Obj {
  if (!v || typeof v !== 'object' || Array.isArray(v)) fail()
  return v as Obj
}
function num(v: unknown): number {
  if (typeof v !== 'number' || !Number.isFinite(v)) fail()
  return v
}
function numOr(v: unknown, d: number): number {
  return v === null || v === undefined ? d : num(v)
}
function numOrNull(v: unknown): number | null {
  return v === null || v === undefined ? null : num(v)
}
function str(v: unknown): string {
  if (typeof v !== 'string') fail()
  return v
}
function strOrNull(v: unknown): string | null {
  return v === null || v === undefined ? null : str(v)
}
/** Money: a decimal string, a number read back into a string, or null. */
function dec(v: unknown): string | null {
  if (v === null || v === undefined) return null
  if (typeof v === 'number' && Number.isFinite(v)) return String(v)
  return str(v)
}
function arr(v: unknown): unknown[] {
  if (!Array.isArray(v)) fail()
  return v
}

function decodeRules(raw: unknown): BinderRules {
  const o = obj(raw)
  return {
    match: MATCH_ALL,
    conditions: arr(o.conditions).map((c): RuleCondition => {
      const { field, op, value, ...rest } = obj(c)
      // A condition with a key this client does not know is kept whole, its
      // value included, so a save sends back exactly what the server stored.
      const unknown = Object.keys(rest).some(k => !KNOWN_CONDITION_KEYS.includes(k))
      // Otherwise as sent: mv / copies / printing_copies a number, everything
      // else strings. Core keys first, so an unchanged rule serializes like
      // the builder's own.
      const v = unknown ? value as RuleCondition['value']
        : Array.isArray(value) ? value.map(x => String(x))
          : typeof value === 'number' ? value : String(value ?? '')
      return { field: str(field), op: str(op), value: v, ...rest }
    }),
  }
}

export function decodeBinder(raw: unknown): Binder {
  const o = obj(raw)
  const coverRaw = o.cover === null || o.cover === undefined ? null : obj(o.cover)
  // Unavailable stats (the spare rule, decks unreadable): the server sends
  // `stats_available: false` with null counts. A count that is explicitly
  // null is read the same way (fail closed): never as 0, which would show
  // an unreadable binder as an empty one.
  const unavailable = o.stats_available === false || o.line_count === null || o.copy_count === null
  const b: Binder = {
    binder_id: numOrNull(o.binder_id),
    kind: str(o.kind) as BinderKind,
    name: str(o.name),
    color: strOrNull(o.color) as BinderColor | null,
    cover: coverRaw ? { scryfall_id: str(coverRaw.scryfall_id), image_art_crop: strOrNull(coverRaw.image_art_crop) } : null,
    position: numOrNull(o.position),
    // A create / patch response carries no counts (absent keys): 0.
    line_count: unavailable ? null : numOr(o.line_count, 0),
    copy_count: unavailable ? null : numOr(o.copy_count, 0),
    value_usd: unavailable ? null : dec(o.value_usd),
    updated_at: strOrNull(o.updated_at),
    // Absent, null or malformed: no link.
    share: decodeBinderShare(o.share),
  }
  if (unavailable) b.stats_available = false
  if (o.rules !== undefined && o.rules !== null) b.rules = decodeRules(o.rules)
  if (typeof o.rules_rev === 'number') b.rules_rev = o.rules_rev
  if (b.kind === 'deck') {
    if (typeof o.import_name === 'string') b.import_name = o.import_name
    if (typeof o.source_key === 'string') b.source_key = o.source_key
    b.outbox_state = known(DECK_OUTBOX_STATES, o.outbox_state)
    b.flags = Array.isArray(o.flags) ? o.flags.map(f => known(DECK_BOX_FLAGS, f)) : []
  }
  return b
}

/** A strict enum with an 'unknown' fallback (a newer server value never fails the decode). */
function known<T extends string>(allowed: readonly T[], v: unknown): T | typeof UNKNOWN {
  return typeof v === 'string' && (allowed as readonly string[]).includes(v) ? v as T : UNKNOWN
}

/** A positive whole number, else the fallback (an additive field never fails the decode). */
function limitOr(v: unknown, d: number): number {
  return typeof v === 'number' && Number.isInteger(v) && v > 0 ? v : d
}

export function decodeLimits(raw: unknown): BinderLimits {
  const o = raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Obj) : {}
  const limits: BinderLimits = {
    manual: limitOr(o.manual, BINDER_LIMITS_DEFAULT.manual),
    smart: limitOr(o.smart, BINDER_LIMITS_DEFAULT.smart),
  }
  // No default: absent (or unusable) means the deck gate is off.
  if (typeof o.deck === 'number' && Number.isInteger(o.deck) && o.deck > 0) limits.deck = o.deck
  // Same for the spare gate: present (a number, or true) turns it on.
  if (typeof o.spare === 'number' && Number.isFinite(o.spare) && o.spare >= 0) limits.spare = o.spare
  else if (o.spare === true) limits.spare = true
  return limits
}

export function decodeBindersList(raw: unknown): BindersList {
  const o = obj(raw)
  return {
    binders_rev: num(o.binders_rev),
    copies_rev: num(o.copies_rev),
    prices_as_of: strOrNull(o.prices_as_of),
    binders: arr(o.binders).map(decodeBinder),
    limits: decodeLimits(o.limits),
    share_enabled: o.share_enabled === true,
  }
}

export function decodeBinderWrite(raw: unknown): BinderWriteResponse {
  const o = obj(raw)
  return { binder: decodeBinder(o.binder), binders_rev: num(o.binders_rev) }
}

export function decodeBinderPreview(raw: unknown): BinderPreview {
  const o = obj(raw)
  // The preview has no nullable contract (decks unreadable is a 503); a
  // server that sends the list's unavailable shape anyway reads the same.
  if (o.stats_available === false) throw new CopiesError(ERR.decksUnavailable)
  return { line_count: num(o.line_count), copy_count: num(o.copy_count), value_usd: dec(o.value_usd) }
}

/** A product photo URL: an absolute https URL, else null (absent, empty or odd). */
function imageUrl(v: unknown): string | null {
  return typeof v === 'string' && /^https:\/\/\S+$/.test(v) ? v : null
}

export function decodeSealedItem(raw: unknown): SealedItem {
  const o = obj(raw)
  return {
    sealed_id: num(o.sealed_id),
    product_uuid: strOrNull(o.product_uuid),
    name: str(o.name),
    set_code: strOrNull(o.set_code),
    category: strOrNull(o.category),
    qty: num(o.qty),
    status: str(o.status) as SealedStatus,
    purchase_price: dec(o.purchase_price),
    unit_value_usd: dec(o.unit_value_usd),
    value_usd: dec(o.value_usd),
    paid_usd: dec(o.paid_usd),
    gain_usd: dec(o.gain_usd),
    stale: o.stale === true,
    buy_url: strOrNull(o.buy_url),
    image_url: imageUrl(o.image_url),
    added_at: typeof o.added_at === 'string' ? o.added_at : '',
    updated_at: typeof o.updated_at === 'string' ? o.updated_at : '',
  }
}

export function decodeSealedList(raw: unknown): SealedList {
  const o = obj(raw)
  const t = obj(o.totals)
  return {
    items: arr(o.items).map(decodeSealedItem),
    totals: {
      paid_usd: dec(t.paid_usd),
      value_usd: dec(t.value_usd),
      unpriced_rows: numOr(t.unpriced_rows, 0),
      unpriced_units: numOr(t.unpriced_units, 0),
    },
  }
}

export function decodeSealedWrite(raw: unknown): SealedWriteResponse {
  const o = obj(raw)
  const r: SealedWriteResponse = {
    item: o.item === null || o.item === undefined ? null : decodeSealedItem(o.item),
    merged: o.merged === true,
    result_sealed_id: numOrNull(o.result_sealed_id),
  }
  if (typeof o.deleted === 'boolean') r.deleted = o.deleted
  return r
}

export function decodeSealedOpen(raw: unknown): SealedOpenResponse {
  const o = obj(raw)
  return { opened: num(o.opened), remaining: num(o.remaining), sealed_id: numOrNull(o.sealed_id) }
}

export function decodeSealedProduct(raw: unknown): SealedProduct {
  const o = obj(raw)
  return {
    uuid: str(o.uuid),
    name: str(o.name),
    set_code: strOrNull(o.set_code),
    category: strOrNull(o.category),
    subtype: strOrNull(o.subtype),
    release_date: strOrNull(o.release_date),
    ck_retail_usd: dec(o.ck_retail_usd),
    price_updated_at: strOrNull(o.price_updated_at),
    stale: o.stale === true,
    image_url: imageUrl(o.image_url),
  }
}

// --- Endpoints: binders -----------------------------------------------------------

const NEUTRAL = { capabilityNeutral: true } as const

function withKey<T extends object>(body: T, opKey: string | undefined): T & { op_key?: string } {
  return opKey ? { ...body, op_key: opKey } : body
}

/** `GET /binders` (spec 3.2): manual and smart binders by position, then Unsorted. */
export async function listBinders(ctx: CopiesContext): Promise<BindersList> {
  const res = await copiesFetch(ctx, { method: HTTP.get, sub: SEG.binders, ...NEUTRAL })
  return decodeBindersList(await responseJson(res))
}

/** `POST /binders` (spec 3.3). */
export async function createBinder(ctx: CopiesContext, body: BinderCreateBody, opKey?: string): Promise<BinderWriteResponse> {
  const res = await copiesFetch(ctx, { method: HTTP.post, sub: SEG.binders, body: withKey(body, opKey), ...NEUTRAL })
  return decodeBinderWrite(await responseJson(res))
}

/** `PATCH /binders/{binder_id}` (spec 3.3). */
export async function patchBinder(ctx: CopiesContext, binderId: number, body: BinderPatchBody, opKey?: string): Promise<BinderWriteResponse> {
  const res = await copiesFetch(ctx, { method: HTTP.patch, sub: `${SEG.binders}/${binderId}`, body: withKey(body, opKey), ...NEUTRAL })
  return decodeBinderWrite(await responseJson(res))
}

/** `DELETE /binders/{binder_id}` (spec 3.4): 204, idempotent, no op_key. */
export async function deleteBinder(ctx: CopiesContext, binderId: number): Promise<void> {
  await copiesFetch(ctx, { method: HTTP.delete, sub: `${SEG.binders}/${binderId}`, ...NEUTRAL })
}

/** `PUT /binders/order` (spec 3.3): 409 binders_rev_mismatch (with binders_rev) when stale. */
export async function orderBinders(
  ctx: CopiesContext, binderIds: number[], expectedRev: number, opKey?: string,
): Promise<{ binders_rev: number }> {
  const res = await copiesFetch(ctx, {
    method: HTTP.put,
    sub: SEG.bindersOrder,
    body: withKey({ binder_ids: binderIds, expected_binders_rev: expectedRev }, opKey),
    ...NEUTRAL,
  })
  return { binders_rev: num(obj(await responseJson(res)).binders_rev) }
}

/** `POST /binders/preview` (spec 3.7): an unsaved rule set's counts. 10/min. */
export async function previewBinder(ctx: CopiesContext, rules: BinderRules): Promise<BinderPreview> {
  // Paced by the caller (lib/previewLimiter): never auto-retried here.
  const res = await copiesFetch(ctx, { method: HTTP.post, sub: SEG.bindersPreview, body: { rules }, noBackoff: true, ...NEUTRAL })
  return decodeBinderPreview(await responseJson(res))
}

// --- Endpoints: sealed ------------------------------------------------------------

/** `GET /sealed` (spec 5.3): items, newest first, and totals over owned rows. */
export async function listSealed(ctx: CopiesContext): Promise<SealedList> {
  const res = await copiesFetch(ctx, { method: HTTP.get, sub: SEG.sealed, ...NEUTRAL })
  return decodeSealedList(await responseJson(res))
}

/** `POST /sealed`: merges into an identical row (result_sealed_id). */
export async function addSealed(ctx: CopiesContext, body: SealedAddBody, opKey?: string): Promise<SealedWriteResponse> {
  const res = await copiesFetch(ctx, { method: HTTP.post, sub: SEG.sealed, body: withKey(body, opKey), ...NEUTRAL })
  return decodeSealedWrite(await responseJson(res))
}

/** `PATCH /sealed/{sealed_id}`: absolute qty (0 deletes), status, purchase_price. */
export async function patchSealed(ctx: CopiesContext, sealedId: number, body: SealedPatchBody, opKey?: string): Promise<SealedWriteResponse> {
  const res = await copiesFetch(ctx, { method: HTTP.patch, sub: `${SEG.sealed}/${sealedId}`, body: withKey(body, opKey), ...NEUTRAL })
  return decodeSealedWrite(await responseJson(res))
}

/** `DELETE /sealed/{sealed_id}`: 204, idempotent. */
export async function deleteSealed(ctx: CopiesContext, sealedId: number): Promise<void> {
  await copiesFetch(ctx, { method: HTTP.delete, sub: `${SEG.sealed}/${sealedId}`, ...NEUTRAL })
}

/** `POST /sealed/{sealed_id}/open`: 409 insufficient_qty (current_qty), 422 cannot_open_want. */
export async function openSealed(ctx: CopiesContext, sealedId: number, qty: number, opKey?: string): Promise<SealedOpenResponse> {
  const res = await copiesFetch(ctx, {
    method: HTTP.post, sub: `${SEG.sealed}/${sealedId}${SEG.sealedOpen}`, body: withKey({ qty }, opKey), ...NEUTRAL,
  })
  return decodeSealedOpen(await responseJson(res))
}

/**
 * `GET /sealed/catalog?q=&set=` (public, its own gate). `userId` sends the
 * owner's bearer (needed while the catalog is dark for everyone but the
 * allowlist); a session that is not that user sends nothing. A 404 here is
 * the dark gate and never touches the per-copy capability.
 */
export async function fetchSealedCatalog(
  q: string, opts: { set?: string; userId?: string | null; signal?: AbortSignal } = {},
): Promise<SealedProduct[]> {
  const qs = new URLSearchParams({ [CATALOG_QUERY.q]: q })
  if (opts.set) qs.set(CATALOG_QUERY.set, opts.set)
  const url = apiUrl(`${SEG.sealedCatalog}?${qs.toString()}`)
  let headers: Record<string, string> = {}
  if (opts.userId) {
    const auth = await writeAuthFor(url, opts.userId)
    if (auth.ok) headers = auth.headers
  }
  let res: Response
  try {
    res = await apiFetch(url, { method: HTTP.get, headers, signal: opts.signal })
  } catch {
    if (opts.signal?.aborted) throw new CopiesError(ERR.aborted)
    throw new CopiesError(ERR.network)
  }
  if (!res.ok) {
    let body: unknown
    try { body = await res.json() } catch { body = undefined }
    const detail = parseDetail(body)
    throw new CopiesError(classifyFailure(res.status, detail, parseErrorField(body)), { status: res.status, detail })
  }
  return arr(obj(await responseJson(res)).products).map(decodeSealedProduct)
}

/**
 * The Buy link of a sealed item: the API's internal redirect, absolute, with
 * this client as `src`. Only a path the server sent under /sealed/redirect/
 * is ever linked.
 */
export function sealedBuyHref(buyUrl: string | null): string | null {
  if (!buyUrl || !/^\/sealed\/redirect\/[A-Za-z0-9-]+$/.test(buyUrl)) return null
  return apiUrl(`${buyUrl}?src=${BUY_SRC}`)
}

/** The rule field whose answer depends on My Decks. */
const SPARE_FIELD: string = SPARE_GATED_RULE_FIELDS[0]

/**
 * Whether a rule set names the spare rule: its matches (and counts) depend on
 * My Decks, so a deck change invalidates them (lib/deckDependentCollection).
 * Any condition naming the field counts, a read-only kept one included.
 */
export function rulesNameSpare(rules: { conditions?: readonly ({ field?: unknown } | null)[] } | null | undefined): boolean {
  return !!rules?.conditions?.some(c => c?.field === SPARE_FIELD)
}
